import { useMemo } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Check, ChevronRight, Layers } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { LoadFailure } from '@/components/states/index';
import { withReturnPath } from '@/lib/return-path';
import { useServerBodyHandoff, useServerBodyRecovery } from '@/lib/server-body-handoff';
import {
  Body,
  Button,
  Caption,
  Card,
  CardGrid,
  EmptyState,
  Heading,
  I,
  Icon,
  Label,
  Pill,
  Row,
  ScreenBody,
  Skeleton,
  SkeletonGroup,
  SkeletonText,
  Stack,
  TONE,
  useDelayedLoading,
} from '@leanin/ui';

import { RouterLinkAs } from '@/lib/router-link';
import { useMember } from '@/lib/use-session';
import { toneFromId } from '@/lib/group-tone';
import { TRACK_TONES } from './LearningTrackCard';
import styles from './LearningTrackDetailView.module.css';
import {
  useLearningTrack,
  type LearningTrackDetail,
  type LearningTrackItem,
} from '@/lib/queries/learning-tracks';
import { useSetTrackItemProgress } from '@/lib/mutations/learning-tracks';
import { Toggle } from '@leanin/ui/client';

// Curriculum view of a learning track: hero (cover + badge + title + start),
// viewer progress bar, then the items as a numbered sequence — thumbnail,
// minutes, title, lede, a completion check (signed-in only), and a
// chevron into the resource itself.

type Props = {
  readonly trackId: string;
  readonly canManage: boolean;
};

export function LearningTrackDetailView({ trackId, canManage }: Props) {
  const t = useTranslations('LearningTracks');
  // The route loader already ran the fetch, so a failure it left behind is
  // not refetched on mount: the retry surface refetches it on demand.
  const trackQuery = useLearningTrack(trackId, { retryOnMount: false });
  const signedIn = useMember() !== null;
  // A server-composed track page hands off once its track has loaded. A
  // failed load is settled by `LoadFailure` below; a kept page then tries the
  // track again.
  useServerBodyHandoff(trackQuery.isSuccess);
  useServerBodyRecovery(trackQuery);

  const track = trackQuery.data;
  const showSkeleton = useDelayedLoading(track === undefined && !trackQuery.isError);
  if (track === undefined) {
    if (trackQuery.isError) {
      return (
        <LoadFailure
          error={trackQuery.error}
          loadError={{ title: t('detail.loadError.title'), body: t('detail.loadError.body') }}
        />
      );
    }
    return showSkeleton ? <LearningTrackDetailSkeleton /> : null;
  }

  return (
    <ScreenBody gap="none">
      <Stack gap="xl" pt="lg">
        <Row gap="sm" align="center" justify="space-between">
          <Button
            href="/resources"
            linkAs={RouterLinkAs}
            variant="secondary"
            size="sm"
            icon={I.back}
          >
            {t('detail.back')}
          </Button>
          <Row gap="sm" align="center">
            {!track.published ? (
              <Pill tone="neutral">{t('detail.draft')}</Pill>
            ) : null}
            {canManage ? (
              <Button
                href={`/admin/resources/learning-tracks/${track.id}`}
                linkAs={RouterLinkAs}
                variant="secondary"
                size="sm"
                icon={I.edit}
              >
                {t('detail.manage')}
              </Button>
            ) : null}
          </Row>
        </Row>

        <TrackHero track={track} />

        {signedIn && track.resourceCount > 0 ? (
          <ProgressBar completed={track.viewerCompletedCount} total={track.resourceCount} />
        ) : null}

        <TrackItems track={track} signedIn={signedIn} />
      </Stack>
    </ScreenBody>
  );
}

function TrackHero({ track }: { readonly track: LearningTrackDetail }) {
  const t = useTranslations('LearningTracks');
  // Same photo the preview card leads with: the explicit cover, falling
  // back to the first item's.
  const cover =
    track.coverImageUrl ??
    track.items.find((i) => i.resource?.coverImageUrl != null)?.resource
      ?.coverImageUrl ??
    null;
  const tone = TONE[toneFromId(track.id, TRACK_TONES)];

  return (
    <div
      className={styles.hero}
      style={{ '--hero-tone': tone.bg } as React.CSSProperties}
    >
      {cover !== null ? (
        <img className={styles.heroImage} src={cover} alt="" />
      ) : null}
      <div className={styles.heroScrim} />
      <Stack gap="md" align="flex-start" className={styles.heroContent}>
        <span className={styles.heroBadge}>
          <Icon size="sm">
            <Layers size="100%" strokeWidth={1.75} />
          </Icon>
          <span>{t('card.badge')}</span>
        </span>
        <Heading level={1} className={styles.heroTitle}>
          {track.title}
        </Heading>
        {track.description.length > 0 ? (
          // Body applies its colour inline, so a class cannot reach it; the
          // on-dark token is passed the same way.
          <Body style={{ color: 'var(--on-dark-fg-strong)' }}>{track.description}</Body>
        ) : null}
        <Caption className={styles.heroMeta}>
          {t('card.resourceCount', { count: track.resourceCount })}
        </Caption>
      </Stack>
    </div>
  );
}

function ProgressBar({
  completed,
  total,
}: {
  readonly completed: number;
  readonly total: number;
}) {
  const t = useTranslations('LearningTracks');
  const pct = total === 0 ? 0 : Math.round((completed / total) * 100);
  return (
    <Row gap="lg" align="center">
      <div
        className={styles.progressTrack}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={completed}
        aria-label={t('detail.progressAria')}
      >
        <div className={styles.progressFill} style={{ width: `${pct}%` }} />
      </div>
      <Caption>
        {t('detail.progressLabel', { completed, total })}
      </Caption>
    </Row>
  );
}

function TrackItems({
  track,
  signedIn,
}: {
  readonly track: LearningTrackDetail;
  readonly signedIn: boolean;
}) {
  const t = useTranslations('LearningTracks');
  const ordered = useMemo(
    () => [...track.items].sort((a, b) => a.sortOrder - b.sortOrder),
    [track.items],
  );

  if (ordered.length === 0) {
    return (
      <Card pad="flush" style={{ overflow: 'hidden' }}>
        <EmptyState
          density="page"
          tone="burgundy"
          icon="book"
          eyebrow={t('detail.emptyEyebrow')}
          title={t('detail.emptyTitle')}
          body={t('detail.emptyBody')}
        />
      </Card>
    );
  }

  return (
    <Stack gap="none">
      {ordered.map((item, index) => {
        const position = index + 1;
        switch (item.availability) {
          case 'available':
            return (
              <AvailableItemRow
                key={item.resourceId}
                trackId={track.id}
                item={item}
                position={position}
                signedIn={signedIn}
              />
            );
          case 'sign_in_required':
            return <SignInRequiredItemRow key={item.resourceId} position={position} />;
          case 'unavailable':
            return <UnavailableItemRow key={item.resourceId} position={position} />;
        }
      })}
    </Stack>
  );
}

// A signed-out visitor's placeholder for an item any signed-in member could
// open. Signing in returns them to this track.
function SignInRequiredItemRow({ position }: { readonly position: number }) {
  const t = useTranslations('LearningTracks');
  const here = useLocation({ select: (location) => location.href });
  return (
    <div className={styles.itemRow}>
      <span className={styles.itemNumber}>{position}</span>
      <Stack gap="xs" className={styles.itemText}>
        <Label>{t('detail.signInRequired')}</Label>
        <Caption>{t('detail.signInRequiredBody')}</Caption>
      </Stack>
      <Button
        href={withReturnPath('/sign-in', here)}
        linkAs={RouterLinkAs}
        variant="secondary"
        size="md"
        icon={I.lock}
      >
        {t('detail.signIn')}
      </Button>
    </div>
  );
}

// Unpublished, held by moderation, or gated to a Network the viewer is not
// in. The reason is deliberately not disclosed.
function UnavailableItemRow({ position }: { readonly position: number }) {
  const t = useTranslations('LearningTracks');
  return (
    <div className={styles.itemRow}>
      <span className={styles.itemNumber}>{position}</span>
      <Body size="sm" color="mute" className={styles.itemText}>
        {t('detail.unavailable')}
      </Body>
    </div>
  );
}

function AvailableItemRow({
  trackId,
  item,
  position,
  signedIn,
}: {
  readonly trackId: string;
  readonly item: Extract<LearningTrackItem, { availability: 'available' }>;
  readonly position: number;
  readonly signedIn: boolean;
}) {
  const t = useTranslations('LearningTracks');
  const navigate = useNavigate();
  const progress = useSetTrackItemProgress(trackId);
  const resource = item.resource;

  return (
    // Whole-row click opens the resource (the chevron stays as the
    // accessible link); nested controls stop propagation.
    <div
      className={styles.itemRow}
      data-clickable
      onClick={() => void navigate({ to: `/resources/${resource.id}` })}
    >
      <span className={styles.itemNumber}>{position}</span>
      <div
        className={styles.itemThumb}
        style={
          {
            '--thumb-tone': TONE[toneFromId(resource.id, TRACK_TONES)].bg,
          } as React.CSSProperties
        }
      >
        {resource.coverImageUrl !== null ? (
          <img src={resource.coverImageUrl} alt="" />
        ) : null}
      </div>
      <Stack gap="xs" className={styles.itemText}>
        {/* eslint-disable-next-line @leanin/ui/typography-component-required -- same semibold title exception as the preview cards */}
        <h3 className={styles.itemTitle}>{resource.title}</h3>
        {resource.lede !== null && resource.lede.length > 0 ? (
          <Body size="sm" color="mute" className={styles.itemLede}>
            {resource.lede}
          </Body>
        ) : null}
      </Stack>
      {/* Nested controls own their clicks — keep the row-click handler
          from also firing under the check / chevron. */}
      <Row gap="sm" align="center" className={styles.itemActions} onClick={(e) => e.stopPropagation()}>
        {signedIn ? (
          <Toggle
            pressed={item.viewerCompleted}
            onPressedChange={(next) =>
              progress.mutate({ resourceId: item.resourceId, completed: next })
            }
            disabled={progress.isPending}
            className={styles.checkButton}
            data-completed={item.viewerCompleted}
            aria-label={
              item.viewerCompleted
                ? t('detail.markIncompleteAria', { title: resource.title })
                : t('detail.markCompleteAria', { title: resource.title })
            }
          >
            {item.viewerCompleted ? (
              <>
                <Icon size="sm">
                  <Check size="100%" strokeWidth={2} />
                </Icon>
                {t('detail.completed')}
              </>
            ) : (
              t('detail.markComplete')
            )}
          </Toggle>
        ) : null}
        <Button
          href={`/resources/${resource.id}`}
          linkAs={RouterLinkAs}
          variant="secondary"
          size="md"
          icon={<ChevronRight size="100%" strokeWidth={1.75} />}
          aria-label={t('detail.openResourceAria', { title: resource.title })}
        />
      </Row>
    </div>
  );
}

export function LearningTrackDetailSkeleton() {
  const t = useTranslations('LearningTracks');
  return (
    <SkeletonGroup label={t('detail.loading')}>
      <ScreenBody gap="none">
        <Stack gap="xl" pt="lg">
          <Skeleton width="100%" height={260} rounded="lg" />
          <Skeleton width="100%" height={8} rounded="full" />
          <CardGrid gap="lg">
            {Array.from({ length: 4 }, (_, i) => (
              <Card key={i} pad="default" direction="column">
                <Stack gap="md">
                  <Skeleton width={64} height={20} rounded="sm" />
                  <Skeleton width="80%" height={24} rounded="sm" />
                  <SkeletonText lines={2} size="md" />
                </Stack>
              </Card>
            ))}
          </CardGrid>
        </Stack>
      </ScreenBody>
    </SkeletonGroup>
  );
}
